import { Link } from 'react-router-dom';
import { HomeHeroCarousel } from '@/components/HomeHeroCarousel';
import { ExploreCarousel } from '@/components/ExploreCarousel';
import { CaloriesCard } from '@/components/CaloriesCard';
import { CardioCard } from '@/components/CardioCard';
import { DEMO_CALORIE_STATS, DEMO_CARDIO_STATS } from '@/utils/demoStats';

const BG = '#0A0A0A';
const SURFACE = '#141414';
const ACCENT = '#0096FC';
const TEXT_SEC = '#A3A3A3';
const CTA_BLUE = '#0096FC';
const LIGHT_BLUE = '#63B3FF';

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 mb-4 font-heading text-[11px] md:text-xs font-bold uppercase tracking-[0.2em]"
        style={{ color: LIGHT_BLUE }}>
    <span className="block w-6 h-px" style={{ background: LIGHT_BLUE }} aria-hidden="true" />
    {children}
  </span>
);

const PROGRAMS = [
  {
    id: 'coach',
    img: '/program-coach-built.webp',
    name: 'Coach Built',
    meta: 'Adapts every week',
    blurb: 'Your coach plans every session from your level, equipment and training days, then rewrites '
      + 'next week from what you actually lifted.',
    points: ['Progression handled for you', 'Rebuilds around injuries', 'Any gym, any kit'],
    tint: LIGHT_BLUE,
  },
  {
    id: 'muscle-max',
    img: '/program-muscle-max.webp',
    name: 'Muscle Max',
    meta: '16 weeks · muscle build',
    blurb: 'Three phases — foundation, strength, then maximise — driven by your muscle-building goal, '
      + 'your equipment and your age.',
    points: ['Foundation → strength → peak', 'Progressive overload weekly', 'Continues past week 16'],
    tint: LIGHT_BLUE,
  },
  {
    id: 'booty-max',
    img: '/program-booty-max.webp',
    name: 'Booty Max',
    meta: '12 weeks · glute focus',
    blurb: 'A glute-led plan that still trains the whole body, built around the equipment you really '
      + 'have at home or in the gym.',
    points: ['Glute-focused volume', 'Full-body balance kept', 'Home or full gym'],
    tint: LIGHT_BLUE,
  },
  {
    id: 'custom',
    img: '/program-custom-build.webp',
    name: 'Build Your Own',
    meta: 'Full control',
    blurb: 'Pick your split, your days and your exercises from 2,400+ movements — BodyMax still tracks '
      + 'progression and keeps the plan safe.',
    points: ['Choose every exercise', 'Set your own split', 'Swap whenever you like'],
    tint: LIGHT_BLUE,
  },
];

/** Premium programme reel, placed high on the landing pages. */
export const LandingPrograms = ({ ctaTo = '/register' }) => (
  <section className="relative py-16 md:py-24 overflow-hidden" style={{ background: BG }}
           data-testid="landing-programs-section">
    <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[620px] h-[620px] rounded-full pointer-events-none"
         style={{ background: ACCENT, opacity: 0.045, filter: 'blur(140px)' }} aria-hidden="true" />

    <div className="relative z-[1] max-w-7xl mx-auto px-5 md:px-12">
      <div className="max-w-3xl">
        <Eyebrow>Choose your programme</Eyebrow>
        <h2 className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-[1.05]">
          Four ways to train.<br className="hidden sm:block" /> One brain behind them.
        </h2>
        <p className="mt-4 text-base md:text-lg font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
          Every programme is generated from the same sign-up answers — your level, your goal, your
          equipment and the days you can train. Switch between them whenever you like; your logged
          progress, streaks and personal bests follow you across.
        </p>
      </div>

      <div className="mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-4 md:gap-6 md:overflow-visible"
           style={{ scrollbarWidth: 'none' }} data-testid="landing-programs-track">
        {PROGRAMS.map((p, i) => (
          <article key={p.id}
                   className="snap-center shrink-0 w-[82%] sm:w-[62%] md:w-auto"
                   data-testid={`landing-program-${p.id}`}>
            <div className="h-full flex flex-col rounded-2xl border border-white/[0.08] overflow-hidden"
                 style={{ background: SURFACE, boxShadow: '0 18px 50px rgba(0,0,0,0.5)' }}>
              <div className="relative">
                <img src={p.img} alt={p.name} width={1024} height={1024}
                     loading={i === 0 ? 'eager' : 'lazy'} decoding="async"
                     className="w-full aspect-square object-cover object-center" />
                <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
                     style={{ background: `linear-gradient(to top, ${SURFACE}, transparent)` }} />
              </div>
              <div className="flex flex-1 flex-col px-4 pb-5 -mt-6 relative">
                <h3 className="font-heading text-xl md:text-2xl font-black uppercase tracking-tight text-white">
                  {p.name}
                </h3>
                <p className="mt-1 font-heading text-[11px] font-bold uppercase tracking-[0.16em]"
                   style={{ color: p.tint }}>{p.meta}</p>
                <p className="mt-3 text-sm font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
                  {p.blurb}
                </p>
                <ul className="mt-4 space-y-2">
                  {p.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-[13px] font-medium text-white/70">
                      <span className="mt-[7px] block h-1 w-3 shrink-0 rounded-full" style={{ background: p.tint }} />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <Link to={ctaTo}
              className="w-full sm:w-auto text-center px-7 py-3.5 rounded-xl font-heading text-sm font-black uppercase tracking-[0.1em] text-white active:scale-[0.97] transition-transform"
              style={{ background: CTA_BLUE, boxShadow: '0 10px 30px rgba(0,150,252,0.35)' }}
              data-testid="landing-programs-cta">
          Start your 7-day free trial
        </Link>
        <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Swap programmes any time — nothing you've logged is lost.
        </span>
      </div>
    </div>
  </section>
);

/** What the app actually looks like: the home banner reel and the Explore card. */
export const LandingAppCarousels = ({ ctaTo = '/register' }) => (
  <section className="relative py-20 md:py-28 overflow-hidden" style={{ background: SURFACE }}
           data-testid="landing-carousels-section">
    <div className="absolute -top-24 right-0 w-[460px] h-[460px] rounded-full pointer-events-none"
         style={{ background: ACCENT, opacity: 0.04, filter: 'blur(120px)' }} aria-hidden="true" />

    <div className="relative z-[1] max-w-7xl mx-auto px-5 md:px-12">
      <div className="max-w-2xl">
        <Eyebrow>Inside the app</Eyebrow>
        <h2 className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-[1.05]">
          Your week,<br className="hidden sm:block" /> on one screen
        </h2>
        <p className="mt-4 text-base md:text-lg font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
          Open BodyMax and everything that matters today is already on screen — today's session, your
          week, and what's new. No menus to dig through.
        </p>
      </div>

      <div className="mt-10 grid gap-10 md:gap-8 md:grid-cols-2 md:items-start">
        {/* Home banner reel */}
        <div data-testid="landing-hero-reel">
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-black">
            <HomeHeroCarousel brandBlue />
          </div>
          <h3 className="mt-5 font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-white">
            Built for how you train
          </h3>
          <p className="mt-2 text-base font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
            Strength, cardio, home or full gym — the programmes shown here are the same ones BodyMax
            builds around your level, your equipment and the days you can train.
          </p>
        </div>

        {/* Explore / what's new */}
        <div data-testid="landing-explore-reel">
          <ExploreCarousel ctaTo={ctaTo} brandBlue />
          <h3 className="mt-5 font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-white">
            Recovery, tracked for you
          </h3>
          <p className="mt-2 text-base font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
            Photograph a meal and BodyMax returns the calories, protein, carbs and fat. Water and sleep
            log in a tap, and every target comes from your own weight, goal and training week.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/** The two trackers users see on their dashboard, staggered: calories right, cardio left. */
export const LandingTrackers = () => (
  <section className="relative py-20 md:py-28 overflow-hidden" style={{ background: BG }}
           data-testid="landing-trackers-section">
    <div className="absolute top-1/3 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none"
         style={{ background: '#0A84FF', opacity: 0.05, filter: 'blur(130px)' }} aria-hidden="true" />

    <div className="relative z-[1] max-w-7xl mx-auto px-5 md:px-12">
      <div className="max-w-2xl">
        <Eyebrow>Calories &amp; cardio</Eyebrow>
        <h2 className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-[1.05]">
          Every session counted
        </h2>
        <p className="mt-4 text-base md:text-lg font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
          Energy burned and cardio minutes are worked out from the sets you log and the sessions you
          finish — six weeks at a glance, so you can see the direction, not just today.
        </p>
      </div>

      <div className="mt-12 grid gap-8 md:grid-cols-2 md:gap-10 md:items-start">
        {/* Lower, left */}
        <div className="md:mt-16" data-testid="landing-cardio-demo">
          <div className="mx-auto w-full max-w-md">
            <CardioCard stats={DEMO_CARDIO_STATS} isDemo />
          </div>
          <h3 className="mt-5 font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-white">
            Cardio that keeps score
          </h3>
          <p className="mt-2 text-base font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
            Minutes, sessions, distance and your weekly target — plus the streak you're protecting.
          </p>
        </div>

        {/* Higher, right */}
        <div className="md:-mt-8" data-testid="landing-calories-demo">
          <div className="mx-auto w-full max-w-md">
            <CaloriesCard stats={DEMO_CALORIE_STATS} isDemo />
          </div>
          <h3 className="mt-5 font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-white">
            Calories from real work
          </h3>
          <p className="mt-2 text-base font-medium leading-relaxed" style={{ color: TEXT_SEC }}>
            Burn is estimated from the weight, reps and effort you actually logged — not a guess from
            your step count.
          </p>
        </div>
      </div>

      <p className="mt-10 text-sm font-medium" style={{ color: 'rgba(255,255,255,0.35)' }}
         data-testid="landing-trackers-note">
        Sample numbers shown — your own fill in from your first logged session.
      </p>
    </div>
  </section>
);
